import "./global.css";
import "./utils/performance";

import { Toaster } from "@/components/ui/toaster";
import { createRoot } from "react-dom/client";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";

import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import EmailVerificationGuard from "./components/EmailVerificationGuard";
import AuthGuard from "./components/AuthGuard";
import AdminAuthGuard from "./components/AdminAuthGuard";
import GuestOnlyGuard from "./components/GuestOnlyGuard";
import ErrorBoundary from "./components/ErrorBoundary";
import ScriptInjectionProvider from "./components/ScriptInjectionProvider";
import { ConversionQueueProvider } from "./contexts/ConversionQueueContext";

// Critical routes - loaded immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Analytics
import Analytics from "./components/Analytics";

// Cookie Consent
import { CookieConsentProvider } from "./hooks/use-cookie-consent";
import { CookieConsentBanner } from "./components/CookieConsentBanner";

// Lazy-loaded components with fallback
const SessionTracker = lazy(() => import("./components/SessionTracker"));
const DynamicConvertPage = lazy(
  () => import("./components/DynamicConvertPage"),
);
const PlaceholderPage = lazy(() => import("./pages/PlaceholderPage"));
const ToolPage = lazy(() => import("./pages/ToolPage"));

// Lazy-load authenticated pages
const MyFilesPage = lazy(() => import("./pages/MyFilesPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const SubscriptionCancel = lazy(() => import("./pages/SubscriptionCancel"));

// Lazy-load public pages
const OCRPage = lazy(() => import("./pages/OCRPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ApiDocsPage = lazy(() => import("./pages/ApiDocsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FormatsPage = lazy(() => import("./pages/FormatsPage"));
const FormatDetailsPage = lazy(() => import("./pages/FormatDetailsPage"));

// Lazy-load auth pages
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));

// Lazy-load legal pages
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const CookiePage = lazy(() => import("./pages/CookiePage"));

// Lazy-load admin layout and pages
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminJobsSimplified = lazy(
  () => import("./pages/admin/AdminJobsSimplified"),
);
const AdminContactMessages = lazy(
  () => import("./pages/admin/AdminContactMessages"),
);
const AdminScriptInjection = lazy(
  () => import("./pages/admin/AdminScriptInjection"),
);
const AdminStorageSettings = lazy(
  () => import("./pages/admin/AdminStorageSettings"),
);
const AdminIntegrations = lazy(() => import("./pages/admin/AdminIntegrations"));
const AdminCloudIntegrations = lazy(
  () => import("./pages/admin/AdminCloudIntegrations"),
);
const AdminOAuthProviders = lazy(
  () => import("./pages/admin/AdminOAuthProviders"),
);
const AdminSubscriptions = lazy(
  () => import("./pages/admin/AdminSubscriptions"),
);
const AdminApiKeys = lazy(() => import("./pages/admin/AdminApiKeys"));
const AdminCleanup = lazy(() => import("./pages/admin/AdminCleanup"));
const AdminEmailSettings = lazy(
  () => import("./pages/admin/AdminEmailSettings"),
);
const AdminStripeSettings = lazy(
  () => import("./pages/admin/AdminStripeSettings"),
);
const AdminSeoSettings = lazy(() => import("./pages/admin/AdminSeoSettings"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[70vh]">
    <div className="animate-pulse text-muted-foreground" aria-hidden="true">
      Loading...
    </div>
  </div>
);

// Converter routes (/convert, /:toolSlug-converter, /:fromFormat-:toFormat)
// lazy-load DynamicConvertPage, whose chunk includes the large ConvertPage
// component + its SEO content. While that chunk downloads, the outer
// PageLoader (min-h-[70vh], ~700-900px) is what paints — but the real
// converter page (breadcrumbs + H1/H2 + banner + upload card + SEO content
// + format tables) renders at roughly 3800-4500px. Footer sits right after
// this Suspense boundary in the DOM, so when the tiny fallback gets
// replaced by the real multi-thousand-pixel page, the footer gets shoved
// down by thousands of pixels — this was the dominant, page-content-
// independent CLS source that survived every other fix (~0.23 on every
// converter page, since every one goes through this same transition). A
// nested Suspense boundary around just <DynamicConvertPage /> with a
// reservation close to the real height stops the footer from being pushed.
const ConverterPageLoader = () => (
  <div className="min-h-[4000px]" aria-hidden="true" />
);

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <CookieConsentProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScriptInjectionProvider>
                <Analytics gtag="G-V0ZY027DDW" />
                <Suspense fallback={null}>
                  <SessionTracker />
                </Suspense>
                <EmailVerificationGuard>
                  <a
                    href="#main-content"
                    className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-primary focus:text-white focus:top-0 focus:left-0"
                  >
                    Skip to main content
                  </a>
                  <div className="min-h-screen flex flex-col">
                    <Navigation />
                    <main id="main-content" className="flex-1 min-h-[70vh]">
                      <Suspense fallback={<PageLoader />}>
                        {/* Wraps <Routes>, not the pages inside them, so
                            navigating between converter routes (a distinct
                            <Route> element each) doesn't remount this
                            provider — the queued files it holds must
                            survive Hierarchical Smart Router redirects. */}
                        <ConversionQueueProvider>
                        <Routes>
                          <Route path="/" element={<Index />} />
                          <Route
                            path="/convert"
                            element={
                              <Suspense fallback={<ConverterPageLoader />}>
                                <DynamicConvertPage />
                              </Suspense>
                            }
                          />
                          <Route path="/ocr" element={<OCRPage />} />
                          <Route path="/formats" element={<FormatsPage />} />
                          <Route
                            path="/formats/:format"
                            element={<FormatDetailsPage />}
                          />
                          <Route path="/pricing" element={<PricingPage />} />
                          <Route path="/my-files" element={<MyFilesPage />} />
                          <Route
                            path="/dashboard"
                            element={
                              <AuthGuard>
                                <DashboardPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="/messages"
                            element={
                              <AuthGuard>
                                <MessagesPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="/subscription"
                            element={
                              <AuthGuard>
                                <SubscriptionPage />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="/subscription/success"
                            element={<SubscriptionSuccess />}
                          />
                          <Route
                            path="/subscription/cancel"
                            element={<SubscriptionCancel />}
                          />

                          {/* Dynamic converter routes - must be before generic toolSlug */}
                          <Route
                            path="/:toolSlug-converter"
                            element={
                              <Suspense fallback={<ConverterPageLoader />}>
                                <DynamicConvertPage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/:fromFormat-:toFormat"
                            element={
                              <Suspense fallback={<ConverterPageLoader />}>
                                <DynamicConvertPage />
                              </Suspense>
                            }
                          />

                          {/* Dynamic tool routes for SEO (non-converter pages) */}
                          <Route path="/:toolSlug" element={<ToolPage />} />

                          <Route path="/api-docs" element={<ApiDocsPage />} />
                          <Route
                            path="/login"
                            element={
                              <GuestOnlyGuard>
                                <LoginPage />
                              </GuestOnlyGuard>
                            }
                          />
                          <Route
                            path="/signup"
                            element={
                              <GuestOnlyGuard>
                                <SignupPage />
                              </GuestOnlyGuard>
                            }
                          />
                          <Route
                            path="/forgot-password"
                            element={<ForgotPasswordPage />}
                          />
                          <Route
                            path="/reset-password"
                            element={<ResetPasswordPage />}
                          />
                          <Route
                            path="/verify-email"
                            element={<VerifyEmailPage />}
                          />
                          <Route path="/contact" element={<ContactPage />} />

                          {/* Admin Routes */}
                          <Route
                            path="/admin"
                            element={
                              <AdminAuthGuard>
                                <AdminLayout />
                              </AdminAuthGuard>
                            }
                          >
                            <Route index element={<AdminDashboard />} />
                            <Route path="users" element={<AdminUsers />} />
                            <Route
                              path="jobs"
                              element={<AdminJobsSimplified />}
                            />
                            <Route
                              path="integrations"
                              element={<AdminIntegrations />}
                            />
                            <Route
                              path="cloud-integrations"
                              element={<AdminCloudIntegrations />}
                            />
                            <Route
                              path="oauth-providers"
                              element={<AdminOAuthProviders />}
                            />
                            <Route
                              path="subscriptions"
                              element={<AdminSubscriptions />}
                            />
                            <Route path="api-keys" element={<AdminApiKeys />} />
                            <Route
                              path="contact"
                              element={<AdminContactMessages />}
                            />
                            <Route
                              path="email"
                              element={<AdminEmailSettings />}
                            />
                            <Route
                              path="stripe"
                              element={<AdminStripeSettings />}
                            />
                            <Route path="seo" element={<AdminSeoSettings />} />
                            <Route
                              path="scripts"
                              element={<AdminScriptInjection />}
                            />
                            <Route
                              path="storage"
                              element={<AdminStorageSettings />}
                            />
                            <Route path="cleanup" element={<AdminCleanup />} />
                            <Route
                              path="settings"
                              element={
                                <PlaceholderPage title="Global Settings" />
                              }
                            />
                          </Route>

                          <Route
                            path="/help"
                            element={<PlaceholderPage title="Help Center" />}
                          />
                          <Route path="/privacy" element={<PrivacyPage />} />
                          <Route path="/terms" element={<TermsPage />} />
                          <Route path="/security" element={<SecurityPage />} />
                          <Route path="/cookies" element={<CookiePage />} />

                          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                        </ConversionQueueProvider>
                      </Suspense>
                    </main>
                    <Footer />
                    <CookieConsentBanner />
                  </div>
                </EmailVerificationGuard>
              </ScriptInjectionProvider>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </CookieConsentProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;

// Ensure single root creation
let root: any = null;

function initializeApp() {
  const container = document.getElementById("root");
  if (!container) {
    console.error("Root container not found");
    return;
  }

  // Only create root if it doesn't exist
  if (!root) {
    root = createRoot(container);
  }

  root.render(<App />);
}

// Initialize app
if (typeof window !== "undefined") {
  initializeApp();
}

// Handle hot module replacement in development
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    if (root) {
      root.render(<App />);
    }
  });
}
